<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="700px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card :height="isEditable ? 440 : 'auto'">
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template>
                        Materiały procesu - <strong>{{ task.process.name }}</strong>
                    </template>
                </h1>
            </v-card-title>
            <v-card-text>
                <v-menu
                    v-if="isEditable"
                    offset-y
                >
                    <template #activator="{on,attrs}">
                        <v-btn
                            color="secondary"
                            v-bind="attrs"
                            depressed
                            data-sentry-label="Dodawanie materiału"
                            v-on="on"
                        >
                            <v-icon left>
                                mdi-plus
                            </v-icon>
                            Dodaj
                        </v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item
                            v-for="(material, index) in availableMaterials"
                            :key="index"
                            @click="addMaterial(material)"
                        >
                            <v-list-item-title>{{ material.name }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <v-data-table
                    v-if="isEditable"
                    :items="materials"
                    :headers="headers"
                    disable-pagination
                    hide-default-footer
                    :mobile-breakpoint="0"
                    no-data-text="Nie dodano materiałów"
                >
                    <template #item.name="{item}">
                        {{ item.material.name }}
                    </template>
                    <template #item.materialIndex="{item}">
                        {{ item.material.materialIndex }}
                    </template>
                    <template #item.usedPercent="{item}">
                        <v-edit-dialog
                            v-if="item.materialType.usedPercentRequired"
                            :return-value.sync="item.usedPercent"
                            persistent
                            large
                            cancel-text="Anuluj"
                            save-text="Zapisz"
                        >
                            {{ item.usedPercent }}
                            <v-icon color="blue" small>
                                mdi-pencil
                            </v-icon>
                            <template #input>
                                <v-text-field
                                    v-model="item.usedPercent"
                                    v-mask="'###'"
                                    single-line
                                />
                            </template>
                        </v-edit-dialog>
                        <template v-else>
                            n/d
                        </template>
                    </template>
                    <template #item.unit="{item}">
                        {{ item.material.unitOfMeasure.name }}
                    </template>
                    <template #item.actions="{item}">
                        <div class="d-inline-flex">
                            <v-tooltip :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-btn
                                        icon
                                        data-sentry-label="Usuń"
                                        v-on="on"
                                        @click="removeMaterial(item)"
                                    >
                                        <v-icon color="pink">
                                            mdi-delete
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>Usuń</span>
                            </v-tooltip>
                        </div>
                    </template>
                </v-data-table>

                <v-data-table
                    v-else
                    :items="materials"
                    :headers="readOnlyHeaders"
                    disable-pagination
                    hide-default-footer
                    :mobile-breakpoint="0"
                    no-data-text="Nie dodano materiałów"
                >
                    <template #item.name="{item}">
                        {{ item.material.name }}
                    </template>
                    <template #item.materialIndex="{item}">
                        {{ item.material.materialIndex }}
                    </template>
                    <template #item.usedPercent="{item}">
                        {{ item.materialType.usedPercentRequired ? item.usedPercent : 'n/d' }}
                    </template>
                    <template #item.unit="{item}">
                        {{ item.material.unitOfMeasure.name }}
                    </template>
                </v-data-table>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    v-if="isEditable"
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-btn
                    v-else
                    color="info"
                    text
                    data-sentry-label="Zamknij"
                    @click="onClose"
                >
                    Zamknij
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="isEditable"
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    @click="onSave"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { appConfig } from '@/config'
import tooltipDelay from '@/mixins/tooltip'
import { mask } from 'vue-the-mask'

export default {
    name: 'MaterialsDialog',
    directives: { mask },
    mixins: [ tooltipDelay ],
    props: {
        isOpen: {
            type: Boolean
        },
        isEditable: {
            type: Boolean
        },
        taskData: {
            type: Object,
            default: null
        },
        taskMaterialsData: {
            type: Array,
            default: () => []
        },
        availableMaterials: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            task: {
                process: {}
            },
            materials: [],
            headers: [
                {
                    text: 'Nazwa',
                    value: 'name',
                    align: 'left',
                    sortable: false,
                    width: 180,
                    showOnReadOnly: true
                },
                {
                    text: 'Indeks materiałowy',
                    value: 'materialIndex',
                    align: 'left',
                    sortable: false,
                    showOnReadOnly: true
                },
                {
                    text: 'Pokrycie (%)',
                    value: 'usedPercent',
                    align: 'center',
                    sortable: false,
                    width: 110,
                    showOnReadOnly: true
                },
                {
                    text: 'Jednostka',
                    value: 'unit',
                    align: 'center',
                    sortable: false,
                    width: 120,
                    showOnReadOnly: true
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'right',
                    sortable: false,
                    width: 100,
                    showOnReadOnly: false
                },
            ]
        }
    },
    computed: {
        readOnlyHeaders() {
            return this.headers.filter(item => item.showOnReadOnly)
        }
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                this.task = JSON.parse(JSON.stringify(this.taskData))
                this.materials = JSON.parse(JSON.stringify(this.taskMaterialsData))
                this.handleMaterialsIndex()
            }
        }
    },
    methods: {
        addMaterial(material) {
            this.materials.push({
                unitOfMeasure: material.unitOfMeasure,
                unitOfMeasureId: material.unitOfMeasureId,
                material: material,
                materialId: material.id,
                materialType: material.materialType,
                materialTypeId: material.materialTypeId,
                usedPercent: setDefaultUsedPercent(material.materialTypeId)
            })

            function setDefaultUsedPercent(colorType) {
                switch (colorType) {
                    case 10:
                        return appConfig.common.defaultMaterialUsedPercents.uv
                    default:
                        return 0
                }
            }

            this.handleMaterialsIndex()
        },
        removeMaterial(material) {
            this.materials.splice(material.index, 1)
            this.handleMaterialsIndex()
        },
        handleMaterialsIndex() {
            this.materials = this.materials.map((item, index) => ({
                ...item,
                index
            }))
        },
        onSave() {
            this.$emit('on-save', this.taskData, this.materials)

            this.onClose()
        },
        onClose() {
            setTimeout(() => {
                this.task = {
                    process: {}
                }
                this.materials = []
            }, 500)

            this.$emit('on-close')
        }
    }
}
</script>